import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Info, MessagesSquare, Newspaper, Briefcase, Phone, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, timeAgo } from "@/lib/api";
import { useAuth, useRequireAuth } from "@/context/AuthContext";
import { MedLyncVerifiedBadge } from "@/components/Badges";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const TABS = [
  { id: "hub-info", value: "information", label: "Information", icon: Info },
  { id: "hub-discussion", value: "discussion", label: "Discussion", icon: MessagesSquare },
  { id: "hub-updates", value: "updates", label: "Updates", icon: Newspaper },
  { id: "hub-opportunities", value: "opportunities", label: "Opportunities", icon: Briefcase },
  { id: "hub-connect", value: "connect", label: "Connect", icon: Phone },
];

function PostComposer({ slug, type, onDone }) {
  const { user } = useAuth();
  const requireAuth = useRequireAuth();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const adminOnly = type === "updates" || type === "opportunities";
  if (adminOnly && (!user || user.role !== "admin")) return null;

  const submit = async () => {
    if (!title.trim() || !body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/hubs/${slug}/posts`, { type, title, body });
      toast.success("Posted");
      setTitle(""); setBody(""); setOpen(false); onDone();
    } catch { toast.error("Could not post"); } finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid={`hub-add-${type}`} onClick={() => requireAuth(() => setOpen(true))}
        className="mb-3 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 py-2.5 text-sm font-semibold text-slate-600 hover:border-medorange hover:text-medorange">
        <Plus className="h-4 w-4" /> {type === "discussion" ? "Start a discussion" : `Add ${type}`}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading capitalize">New {type}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" />
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Details…" className="min-h-[100px]" />
            <Button onClick={submit} disabled={busy} className="w-full bg-medorange hover:bg-medorange-hover">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PostList({ posts }) {
  if (!posts) return <Skeleton className="h-24 w-full rounded-xl" />;
  if (posts.length === 0) return <p className="py-8 text-center text-sm text-slate-400">Nothing here yet.</p>;
  return (
    <div className="space-y-3">
      {posts.map((p) => (
        <div key={p.id} className="ml-rise rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-start justify-between gap-2">
            <h4 className="font-heading text-sm font-bold text-slate-900">{p.title}</h4>
            {p.medlync_verified && <MedLyncVerifiedBadge compact />}
          </div>
          <p className="mt-1 text-sm text-slate-600">{p.body}</p>
          <p className="mt-2 text-[11px] text-slate-400">{p.author_name} · {timeAgo(p.created_at)}</p>
        </div>
      ))}
    </div>
  );
}

export default function GovtHealthHub() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [hub, setHub] = useState(null);
  const [tab, setTab] = useState("information");
  const [posts, setPosts] = useState({});

  useEffect(() => { api.get(`/hubs/${slug}`).then(({ data }) => setHub(data)).catch(() => navigate("/govt-health")); }, [slug, navigate]);

  const loadPosts = useCallback(async (type) => {
    const { data } = await api.get(`/hubs/${slug}/posts`, { params: { type } });
    setPosts((p) => ({ ...p, [type]: data }));
  }, [slug]);

  useEffect(() => {
    if (["discussion", "updates", "opportunities"].includes(tab)) loadPosts(tab);
  }, [tab, loadPosts]);

  if (!hub) return <div className="p-4"><Skeleton className="h-48 w-full rounded-2xl" /></div>;

  return (
    <div className="pb-8">
      <div className="relative">
        {hub.image_url && <img src={fileUrl(hub.image_url)} alt="" className="h-40 w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-navy/90 to-navy/20" />
        <button onClick={() => navigate("/govt-health")} className="absolute left-3 top-3 rounded-full bg-white/90 p-2 text-navy shadow"><ArrowLeft className="h-4 w-4" /></button>
        <div className="absolute bottom-3 left-4 right-4 text-white">
          <h1 className="font-heading text-xl font-extrabold">{hub.name}</h1>
          <p className="line-clamp-2 text-xs text-slate-200">{hub.short_desc}</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-3">
        <TabsList className="no-scrollbar mx-4 flex h-auto w-[calc(100%-2rem)] justify-start gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1">
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value} data-testid={t.id}
              className="shrink-0 gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-medorange">
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="px-4 py-4">
          <TabsContent value="information" className="space-y-3">
            {hub.info_sections?.map((s, i) => (
              <div key={i} className="ml-rise rounded-xl border border-slate-200 bg-white p-4">
                <h3 className="mb-1 font-heading text-sm font-bold text-navy">{s.heading}</h3>
                <p className="text-sm leading-relaxed text-slate-600">{s.content}</p>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="discussion">
            <PostComposer slug={slug} type="discussion" onDone={() => loadPosts("discussion")} />
            <PostList posts={posts.discussion} />
          </TabsContent>
          <TabsContent value="updates">
            <PostComposer slug={slug} type="updates" onDone={() => loadPosts("updates")} />
            <PostList posts={posts.updates} />
          </TabsContent>
          <TabsContent value="opportunities">
            <PostComposer slug={slug} type="opportunities" onDone={() => loadPosts("opportunities")} />
            <PostList posts={posts.opportunities} />
          </TabsContent>

          <TabsContent value="connect" className="space-y-3">
            {hub.connect?.length ? hub.connect.map((c, i) => (
              <div key={i} className="ml-rise flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-navy text-white"><Phone className="h-4 w-4" /></span>
                <div className="min-w-0">
                  <p className="truncate font-heading text-sm font-bold text-slate-900">{c.name}</p>
                  <p className="text-xs text-slate-500">{c.role} · {c.dept}</p>
                  <p className="truncate text-xs text-medorange">{c.contact}</p>
                </div>
              </div>
            )) : <p className="py-8 text-center text-sm text-slate-400">No directory entries yet.</p>}
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
